// src/components/pages/admin-dash/AdminSystemHealth.jsx
import React, { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Badge from "../../ui/Badge";
import Icon from "../../ui/Icon";
import Spinner from "../../ui/Spinner";

/**
 * "Is anything on fire?" rollup — Cache, Redis, Scheduled jobs, and
 * Scratch-off ingestion each already have their own detail screen; this
 * pulls the one status flag each of those cares about into a single view,
 * so nobody has to open three tabs to answer "are we healthy right now".
 * 3-POS_FASE5_ROADMAP.md item 4.2. Backend: GET /admin/system-health.
 */
export default function AdminSystemHealth({ onNavigate, onShowToast }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/system-health");
      setHealth(res.data);
    } catch {
      onShowToast?.("Couldn't load system health.", "error");
    } finally {
      setLoading(false);
    }
  }, [onShowToast]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
            System health
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            One snapshot of cache, Redis, scheduled jobs, and scratch-off ingestion.
          </p>
        </div>
        <Button icon="refresh" size="sm" variant="ghost" onClick={fetchHealth} loading={loading}>
          Refresh
        </Button>
      </header>

      {loading && !health ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <Spinner size={18} />
          </div>
        </Card>
      ) : !health ? (
        <Card>
          <p className="text-[13px] py-4 text-center" style={{ color: "var(--txt-2)" }}>
            Couldn't load system health.
          </p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <HealthCard
            icon="database"
            title="Cache"
            healthy={health.cacheHealthy}
            unhealthyLabel={!health.cacheEnabled ? "Disabled" : "Not loaded"}
            detail={
              <>
                Backend: <strong style={{ color: "var(--txt)" }}>{health.cacheBackend}</strong>
                {health.cacheFallbackBackend && (
                  <> (fallback: {health.cacheFallbackBackend})</>
                )}
              </>
            }
            onClick={() => onNavigate?.("cache")}
          />

          <HealthCard
            icon="server-2"
            title="Redis"
            healthy={health.redisHealthy}
            unhealthyLabel="Unreachable"
            neutral={!health.redisInUse && !health.redisHealthy}
            detail={
              health.redisInUse
                ? "Actively used by the cache."
                : "Not in use — active backend is caffeine, so this being down doesn't affect the app right now."
            }
          />

          <HealthCard
            icon="clock"
            title="Scheduled jobs"
            healthy={health.jobsHealthy}
            unhealthyLabel={`${health.failingJobs?.length ?? 0} failing`}
            detail={
              health.jobsHealthy
                ? `All ${health.totalJobs ?? 0} jobs' latest run succeeded.`
                : `Failing: ${health.failingJobs?.join(", ") || "—"}`
            }
            onClick={() => onNavigate?.("jobRuns")}
          />

          <HealthCard
            icon="ticket"
            title="Scratch-off ingestion"
            healthy={health.scratchHealthy}
            unhealthyLabel={`${health.failingScratchStates?.length ?? 0} failing`}
            detail={
              health.scratchHealthy
                ? `All ${health.totalScratchStates ?? 0} enabled sources are healthy.`
                : `Failing: ${health.failingScratchStates?.join(", ") || "—"}`
            }
            onClick={() => onNavigate?.("scratchSources")}
          />
        </div>
      )}
    </div>
  );
}

function HealthCard({ icon, title, healthy, unhealthyLabel, detail, neutral = false, onClick }) {
  const badge = neutral
    ? <Badge variant="neutral">N/A</Badge>
    : healthy
      ? <Badge variant="teal">OK</Badge>
      : <Badge variant="red">{unhealthyLabel}</Badge>;

  return (
    <Card
      variant="soft"
      className={onClick ? "transition-all hover:-translate-y-[1px] cursor-pointer" : ""}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="shrink-0 inline-flex items-center justify-center rounded-xl"
            style={{
              width: 38,
              height: 38,
              background: "var(--surf-3)",
              color: "var(--txt-2)",
              border: "1px solid var(--border)",
            }}
          >
            <Icon name={icon} size={18} />
          </div>
          <div className="min-w-0">
            <div className="font-medium" style={{ fontSize: 14, color: "var(--txt)" }}>
              {title}
            </div>
            <div className="text-[12px] mt-0.5" style={{ color: "var(--txt-2)" }}>
              {detail}
            </div>
          </div>
        </div>
        {badge}
      </div>
    </Card>
  );
}
